import { useBrand } from '@ui/config/BrandProvider';
import { isAddressInFairLaunchAllowlist } from '@ui/constants/allowlist';
import {} from '@ui/constants/links';
import { ROUTES } from '@ui/constants/routes';
import { NextLinkWithLocale, useRouter } from '@ui/lib/i18n';
import { Tabs } from '@ui/lib/park/ui/tabs';
import { Text } from '@ui/lib/park/ui/text';
import { getActiveRoute } from '@ui/utils/getActiveRoute';
import { useTranslation } from 'next-i18next';
import React, { memo, useMemo } from 'react';
import { css } from 'styled-system/css';
import { Box, BoxProps, HStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { getRoutesArray } from './utils/getRoutesArray';

const navLinkClass = css({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  textDecoration: 'none',
});

export const tintedIconClass = css({
  filter:
    'brightness(0) saturate(100%) invert(86%) sepia(87%) saturate(728%) hue-rotate(16deg) brightness(104%) contrast(104%)',
});

type TopTabsProps = BoxProps;

export const TopTabs: React.FC<TopTabsProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const brand = useBrand();
  const { address } = useAccount();
  const showFairLaunchTab = isAddressInFairLaunchAllowlist(address);

  const router = useRouter();
  const tabs = useMemo(
    () =>
      getRoutesArray(t).filter(
        (r) =>
          !r.mobileOnly &&
          brand.navItems.includes(r.key) &&
          (r.key !== 'fairlaunch' || showFairLaunchTab),
      ),
    [brand.navItems, showFairLaunchTab, t],
  );

  const activeTab = useMemo(
    () =>
      getActiveRoute(
        tabs.map((v) => v.url),
        router.pathname,
      ) ?? router.pathname,
    [router.pathname, tabs],
  );

  return (
    <Box h="full" {...props}>
      <Tabs.Root
        defaultValue={ROUTES.INDEX}
        h="full"
        variant="text"
        size="md"
        value={activeTab}
      >
        <Tabs.List
          alignItems="center"
          boxShadow="none"
          gap="2px"
          h="full"
          px="0px"
          overflow="visible"
        >
          {tabs.map((route) => (
            <TopTabItem key={route.key} route={route} />
          ))}
        </Tabs.List>
      </Tabs.Root>
    </Box>
  );
};

type RouteItem = {
  key: string;
  url: ROUTES;
  label: React.ReactNode;
  tokenIcon?: string;
  tintIcon?: boolean;
};

/**
 * V2 nav link: text-only (no per-item icons), 13px/500;
 * active = text-primary + 2px `--long` underline on the nav's bottom border.
 */
const TopTabItem = memo<{ route: RouteItem }>(({ route }) => {
  return (
    <Tabs.Trigger
      value={route.url}
      h="full"
      px="12px"
      borderRadius="0"
      position="relative"
      color="v2.text.muted"
      transition="color 120ms ease"
      // #21: hover shows a visible surface tint, matching the TP/SL modal
      // segmented control — not just a text-color change
      _hover={{
        color: 'v2.text.primary',
        bg: 'v2.bg.surface1',
      }}
      _selected={{
        color: 'v2.text.primary !important',
        _after: {
          content: '""',
          position: 'absolute',
          left: '12px',
          right: '12px',
          bottom: '-1px',
          height: '2px',
          background: 'var(--brand-active-nav)',
        },
      }}
    >
      <NextLinkWithLocale href={route.url} className={navLinkClass}>
        <HStack gap="7px" alignItems="center" position="relative">
          {typeof route.label === 'string' ? (
            <Text
              color="currentColor"
              fontSize="13px"
              fontWeight={500}
              lineHeight="1"
              whiteSpace="nowrap"
            >
              {route.label}
            </Text>
          ) : (
            route.label
          )}
        </HStack>
      </NextLinkWithLocale>
    </Tabs.Trigger>
  );
});

TopTabItem.displayName = 'TopTabItem';
